import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { trackVisit, fetchVisits } from '../api';

/**
 * Matrix-style futuristic visitor counter.
 * - HUD frame with corner brackets
 * - Per-digit slot machine roll
 * - Scanline + glitch + matrix rain background
 * - Terminal "DECRYPTING…" reveal
 */
export default function VisitorCounter() {
  const [count, setCount] = useState(null);
  const [display, setDisplay] = useState(0);
  const [phase, setPhase] = useState('boot'); // boot -> decrypt -> done
  const canvasRef = useRef(null);

  // 1. Boot sequence
  useEffect(() => {
    let cancel = false;
    const t1 = setTimeout(() => !cancel && setPhase('decrypt'), 700);
    (async () => {
      const tracked = sessionStorage.getItem('pursue_fr_tracked') === '1';
      const data = tracked ? await fetchVisits() : await trackVisit();
      if (!tracked) sessionStorage.setItem('pursue_fr_tracked', '1');
      if (!cancel && data) setCount(data.total_unique || 0);
    })();
    return () => { cancel = true; clearTimeout(t1); };
  }, []);

  // 2. Animate count up (cubic ease), trigger 'done' after
  useEffect(() => {
    if (count == null) return;
    let raf;
    const start = performance.now();
    const dur = 1800;
    const from = display;
    const to = count;
    const tick = (t) => {
      const k = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (k < 1) raf = requestAnimationFrame(tick);
      else setPhase('done');
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // 3. Matrix rain canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const chars = 'ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍｦｲｸｺｿﾁﾄﾉﾌﾔﾖﾙﾚﾛﾝ0123456789'.split('');
    let w = 0, h = 0, columns = 0, drops = [];
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = Math.floor(w / 14);
      drops = new Array(columns).fill(0).map(() => Math.random() * h / 16);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let raf;
    const draw = () => {
      ctx.fillStyle = 'rgba(2, 8, 16, 0.18)';
      ctx.fillRect(0, 0, w, h);
      ctx.font = '12px ui-monospace, SFMono-Regular, Menlo, monospace';
      for (let i = 0; i < columns; i++) {
        const ch = chars[Math.floor(Math.random() * chars.length)];
        const x = i * 14;
        const y = drops[i] * 16;
        // head bright cyan, tail emerald
        ctx.fillStyle = Math.random() > 0.975 ? 'rgba(178, 246, 255, 0.95)' : 'rgba(34, 211, 173, 0.55)';
        ctx.fillText(ch, x, y);
        if (y > h && Math.random() > 0.96) drops[i] = 0;
        drops[i] += 1;
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  // Pad to at least 5 digits for cyber feel; expand naturally beyond
  const padded = String(display).padStart(5, '0');
  const digits = padded.split('');

  return (
    <section data-testid="visitor-counter-section" className="relative">
      <div className="mx-auto max-w-[1400px] px-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="vc-frame relative rounded-lg overflow-hidden"
        >
          {/* Matrix rain background */}
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-50" />
          {/* Scanlines */}
          <div className="vc-scanlines absolute inset-0 pointer-events-none" />
          {/* Vignette */}
          <div className="absolute inset-0 pointer-events-none"
               style={{ background: 'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.75) 100%)' }} />

          {/* Corner brackets */}
          <span className="vc-corner vc-tl" />
          <span className="vc-corner vc-tr" />
          <span className="vc-corner vc-bl" />
          <span className="vc-corner vc-br" />

          <div className="relative z-10 px-6 py-7 md:px-10 md:py-9 flex flex-col items-center gap-4">
            {/* Top label terminal-style */}
            <div className="flex items-center gap-3 text-emerald-300 mono text-[10px] tracking-[0.3em] uppercase">
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                LINK·SECURE
              </span>
              <span className="text-emerald-500/60">::</span>
              <span>PURSUE_FR / NODE_001</span>
              <span className="text-emerald-500/60">::</span>
              <span>{phase === 'boot' ? 'HANDSHAKE…' : phase === 'decrypt' ? 'DECRYPTING…' : 'STREAM_OK'}</span>
            </div>

            {/* Title */}
            <div className="mono text-[11px] tracking-[0.4em] text-cyan-300/90 uppercase">
              &gt; Connexions uniques détectées
            </div>

            {/* Digit grid */}
            <div className="flex items-center gap-1.5 md:gap-2 my-1" data-testid="visitor-counter-value">
              {digits.map((d, i) => (
                <DigitSlot key={i} digit={d} delay={i * 90} ready={phase !== 'boot'} />
              ))}
              <span className="vc-cursor ml-2" />
            </div>

            {/* Sub readouts (telemetry style) */}
            <div className="grid grid-cols-3 gap-6 md:gap-12 mt-2 mono text-[9px] tracking-[0.2em] uppercase text-cyan-200/80">
              <Readout label="Protocole" value="HTTPS/2 · TLS1.3" />
              <Readout label="Encodage" value="SHA-256 / FP-LOCAL" />
              <Readout label="Télémétrie" value="0.0% · OFFLINE" />
            </div>

            {/* Footer caption */}
            <div className="mono text-[9px] tracking-[0.25em] uppercase text-emerald-300/60 mt-1">
              · Identification anonyme par empreinte navigateur · aucune donnée personnelle conservée ·
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Readout({ label, value }) {
  return (
    <div className="flex flex-col items-center text-center">
      <span className="text-emerald-300/60">{label}</span>
      <span className="text-cyan-100/90 mt-0.5">{value}</span>
    </div>
  );
}

function DigitSlot({ digit, delay, ready }) {
  // 10 digits 0-9 then target. Each digit is 1em high; translate to target.
  const target = parseInt(digit, 10);
  const ROW_H = 48; // px, must match CSS .vc-digit height
  const offset = ready ? -ROW_H * target : -ROW_H * 7; // boot shows random-ish

  return (
    <div className="vc-digit relative" style={{ height: ROW_H }}>
      <div
        className="vc-digit-track"
        style={{
          transform: `translateY(${offset}px)`,
          transitionDelay: `${delay}ms`,
        }}
      >
        {Array.from({ length: 10 }, (_, n) => (
          <span key={n} className="vc-digit-cell">{n}</span>
        ))}
      </div>
      <div className="vc-digit-glow" />
    </div>
  );
}
